import React, { useState } from "react";
import axios from "axios";
import {
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
} from "@mui/material";
import API_BASE_URL from "../config";

const AdminConfig = () => {
  // State for storing pricing values
  const [pricing, setPricing] = useState({
    access: "",
    job_basic: "",
    job_premium: "",
    job_advanced: "",
    services_starter: "",
    services_pro: "",
    services_enterprise: "",
    offer_basic: "",
    offer_pro: "",
    offer_advanced: "",
    event_general: "",
    event_premium: "",
    event_exclusive: "",
    ad_basic: "",
    ad_premium: "",
    ad_advanced: "",
    classifieds_standard: "",
    classifieds_premium: "",
    classifieds_exclusive: "",
  });

  // State for storing enabled/disabled service status
  const [enabledServices, setEnabledServices] = useState({
    jobs: true,
    services: true,
    events: true,
    offers: true,
    classifieds: true,
    ads: true,
  });

  // Handle toggle of service switches
  const handleServiceToggle = (service) => {
    setEnabledServices((prev) => ({ ...prev, [service]: !prev[service] }));
  };

  // Handle saving configuration and posting data to backend
  const handleSaveConfig = async () => {
    try {
      // Prepare payload with pricing and enabled services
      const payload = {
        ...pricing,
        jobs: enabledServices.jobs,
        services: enabledServices.services,
        events: enabledServices.events,
        offers: enabledServices.offers,
        classifieds: enabledServices.classifieds,
        ads: enabledServices.ads,
      };

      // Send POST request to backend
      // const response = await axios.post("http://localhost:5000/api/admin-settings", payload);
      const response = await axios.post(
        `${API_BASE_URL}/admin-settings`,
        payload
      );

      // Handle success
      alert("✅ Configuration Saved Successfully!");
      console.log("Response:", response.data);
    } catch (error) {
      // Handle error
      console.error("🔥 Error saving configuration:", error);
      alert("Something went wrong while saving configuration.");
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 4 }}>
      <Card sx={{ p: 3, boxShadow: 3 }}>
        <CardContent>
          <Typography
            variant="h5"
            color="#42326e"
            fontWeight="bold"
            textAlign="center"
          >
            Admin Configuration
          </Typography>

          {/* Service Management */}
          <Typography variant="h6" sx={{ mt: 3 }}>
            Enable/Disable Services
          </Typography>
          {Object.keys(enabledServices).map((service) => (
            <FormControlLabel
              key={service}
              control={
                <Switch
                  checked={enabledServices[service]}
                  onChange={() => handleServiceToggle(service)}
                />
              }
              label={`${service.charAt(0).toUpperCase() + service.slice(1)}`}
            />
          ))}

          {/* Job Pricing */}
          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: "bold" }}>
            Access to Application
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Access(₹)"
                type="number"
                variant="outlined"
                value={pricing.access}
                onChange={(e) =>
                  setPricing({ ...pricing, access: e.target.value })
                }
              />
            </Grid>
          </Grid>

          {/* Job Pricing */}
          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: "bold" }}>
            Job Pricing
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Job Basic (₹)"
                type="number"
                variant="outlined"
                value={pricing.job_basic}
                onChange={(e) =>
                  setPricing({ ...pricing, job_basic: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Job Premium (₹)"
                type="number"
                variant="outlined"
                value={pricing.job_premium}
                onChange={(e) =>
                  setPricing({ ...pricing, job_premium: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Job Advanced (₹)"
                type="number"
                variant="outlined"
                value={pricing.job_advanced}
                onChange={(e) =>
                  setPricing({ ...pricing, job_advanced: e.target.value })
                }
              />
            </Grid>
          </Grid>

          {/* Service Pricing */}
          <Typography variant="subtitle1" sx={{ mt: 3, fontWeight: "bold" }}>
            Service Pricing
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Service Starter (₹)"
                type="number"
                variant="outlined"
                value={pricing.services_starter}
                onChange={(e) =>
                  setPricing({ ...pricing, services_starter: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Service Pro (₹)"
                type="number"
                variant="outlined"
                value={pricing.services_pro}
                onChange={(e) =>
                  setPricing({ ...pricing, services_pro: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Service Enterprise (₹)"
                type="number"
                variant="outlined"
                value={pricing.services_enterprise}
                onChange={(e) =>
                  setPricing({
                    ...pricing,
                    services_enterprise: e.target.value,
                  })
                }
              />
            </Grid>
          </Grid>

          {/* Offer Pricing */}
          <Typography variant="subtitle1" sx={{ mt: 3, fontWeight: "bold" }}>
            Offer Pricing
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Offer Basic (₹)"
                type="number"
                variant="outlined"
                value={pricing.offer_basic}
                onChange={(e) =>
                  setPricing({ ...pricing, offer_basic: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Offer Pro (₹)"
                type="number"
                variant="outlined"
                value={pricing.offer_pro}
                onChange={(e) =>
                  setPricing({ ...pricing, offer_pro: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Offer Advanced (₹)"
                type="number"
                variant="outlined"
                value={pricing.offer_advanced}
                onChange={(e) =>
                  setPricing({ ...pricing, offer_advanced: e.target.value })
                }
              />
            </Grid>
          </Grid>

          {/* Event Pricing */}
          <Typography variant="subtitle1" sx={{ mt: 3, fontWeight: "bold" }}>
            Event Pricing
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Event General (₹)"
                type="number"
                variant="outlined"
                value={pricing.event_general}
                onChange={(e) =>
                  setPricing({ ...pricing, event_general: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Event Premium (₹)"
                type="number"
                variant="outlined"
                value={pricing.event_premium}
                onChange={(e) =>
                  setPricing({ ...pricing, event_premium: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Event Exclusive (₹)"
                type="number"
                variant="outlined"
                value={pricing.event_exclusive}
                onChange={(e) =>
                  setPricing({ ...pricing, event_exclusive: e.target.value })
                }
              />
            </Grid>
          </Grid>

          {/* Ad Pricing */}
          <Typography variant="subtitle1" sx={{ mt: 3, fontWeight: "bold" }}>
            Ad Pricing
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Ad Basic (₹)"
                type="number"
                variant="outlined"
                value={pricing.ad_basic}
                onChange={(e) =>
                  setPricing({ ...pricing, ad_basic: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Ad Premium (₹)"
                type="number"
                variant="outlined"
                value={pricing.ad_premium}
                onChange={(e) =>
                  setPricing({ ...pricing, ad_premium: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Ad Advanced (₹)"
                type="number"
                variant="outlined"
                value={pricing.ad_advanced}
                onChange={(e) =>
                  setPricing({ ...pricing, ad_advanced: e.target.value })
                }
              />
            </Grid>
          </Grid>

          {/* Classifieds Pricing */}
          <Typography variant="subtitle1" sx={{ mt: 3, fontWeight: "bold" }}>
            Classifieds Pricing
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Classifieds Standard (₹)"
                type="number"
                variant="outlined"
                value={pricing.classifieds_standard}
                onChange={(e) =>
                  setPricing({
                    ...pricing,
                    classifieds_standard: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Classifieds Premium (₹)"
                type="number"
                variant="outlined"
                value={pricing.classifieds_premium}
                onChange={(e) =>
                  setPricing({
                    ...pricing,
                    classifieds_premium: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Classifieds Exclusive (₹)"
                type="number"
                variant="outlined"
                value={pricing.classifieds_exclusive}
                onChange={(e) =>
                  setPricing({
                    ...pricing,
                    classifieds_exclusive: e.target.value,
                  })
                }
              />
            </Grid>
          </Grid>

          {/* Save Button */}
          <Button
            variant="contained"
            fullWidth
            sx={{
              mt: 4,
              backgroundColor: "#42326e",
              color: "white",
              "&:hover": {
                backgroundColor: "black",
                color: "white",
              },
            }}
            onClick={handleSaveConfig}
          >
            Save Settings
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AdminConfig;
